import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IStateProvince } from 'src/app/core/interfaces/common/common/stateProvince.interface';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IDropdownOption } from 'src/app/core/interfaces/common/ui/dropdown-option.interface';
import { IBusinessProfile } from 'src/app/core/interfaces/onboarding/business-profile/business-profile.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageContent } from 'src/app/core/models/common/page-content.model';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { CommonService } from 'src/app/core/services/common/common.service';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { StateOfOrgAction } from 'src/app/core/store/onboarding/actions/business-profile.actions';
import { selectStateOfOrg } from 'src/app/core/store/onboarding/reducers+selectors/business-profile.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { syncComponentHeightResize } from 'src/app/core/ui-helpers/functions/sync-component-height';
import { ToolTipComponent } from 'src/app/shared/common/tool-tip/tool-tip.component';
import { ShellComponent } from 'src/app/shared/onboarding/shell/shell.component';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-state-of-org',
  templateUrl: './state-of-org.component.html',
  styleUrls: ['./state-of-org.component.scss'],
})
export class StateOfOrgComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  form: FormGroup;
  backState: PageState;
  nextState: PageState;
  skipState: PageState;
  homeState: PageState;
  @ViewChild(ShellComponent) shellComponent: ShellComponent;
  @ViewChild(ToolTipComponent) questionTooltipComponent: ToolTipComponent;

  tooltip1Content: IPageContent;
  questionText: string;
  properties$: Observable<any>;
  subscriptions: Subscription[] = [];

  stateOfOrgId: string;
  stateOfOrgName: string;

  payload: IBusinessProfile;
  dropdownHeaderText: 'State of organization';
  states: string[];
  defaultOption: IDropdownOption;
  selectedOption: IDropdownOption;
  options: IDropdownOption[];
  stateProvinceData: IStateProvince[] = [];
  selectedState: string;
  pageStateId: string;

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private fb: FormBuilder,
    private cs: CommonService,
    private ees: EnrollmentExtendedService,
    private store: Store<IAppState>,
    private bf: BusinessProfileService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);

        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            this.ees.updateBusinessProfileStateId(
              params['stateId'],
              this.homeState.id
            );
            this.pageStateId = params['stateId'];
          })
        );
        this.stateProvinceData = data['options'];
        this.populateStateProvince();

        this.properties$ = this.store.select(selectStateOfOrg);
        const sub = this.properties$.subscribe((props) => {
          if (props.stateOfOrg.value) {
            this.defaultOption = {
              label: props.stateOfOrg.value,
              value: props.stateOfOrg.value,
              action: this.anySelect.bind(this),
            };
          }
        });
        sub.unsubscribe();
      })
    );

    // Get payload from service/store
    this.payload = this.bf.getPayload();

    // Update payload with pagestate id of current page
    this.payload.PageStateId = this.pageStateId;

    this.form = this.fb.group({
      stateOfOrg: ['', [Validators.required]],
    });
    if (this.defaultOption) this.selectionChange(this.defaultOption);
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      syncComponentHeightResize(
        this.questionTooltipComponent,
        this.shellComponent.content,
        this.shellComponent.container
      )
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async submit() {
    if (this.form.valid) {
      if (this.selectedState != null) {
        this.payload.StateOfOrganization =
          this.form.get('stateOfOrg')?.value?.value ?? '';

        // Store save
        const idValuePair: { id: string; value: string } = {
          id: this.selectedState,
          value: this.payload.StateOfOrganization,
        };

        // saving to store
        this.store.dispatch(
          StateOfOrgAction({
            stateOfOrg: idValuePair,
          })
        );
        const res = await this.bf.submitBusinessProfileData(this.payload);
        return res.isSuccessful;
      }
    }
    this.form.markAllAsTouched();
    return false;
  }

  populatePageStates(transitions: ITransition[]): void {
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.questionText =
      contentDict.get(ContentIDConstants.Question)?.displayText ??
      'In what state is your business legally registered?';
    this.tooltip1Content =
      contentDict.get(ContentIDConstants.Tooltip1) ?? new PageContent();
  }

  public selectionChange(value: any): void {
    this.selectedState = value.value;
    this.form.get('stateOfOrg')?.setValue(value);
    this.defaultOption = value;
  }
  anySelect() {}

  async populateStateProvince() {
    this.options = this.stateProvinceData.map((item) => ({
      label: item.name,
      value: item.code,
      action: this.anySelect.bind(this),
    }));
  }
}
